// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
import { Box, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Switch, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import type { Lang } from "@/hooks/useLang/types";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyCategory, TyProductDetail } from "@/types";
import InputGroups from "@/UI/components/InputGroups";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import FilesInput from "@/UI/inputs/FilesInput";
import ProductSizePopup from "@/UI/popups/ProductSizePopup";
import type { ProductSizePopupDetails, TyPriceRow } from "@/UI/popups/ProductSizePopup";
import { getTextFieldSx, hasAnyLangValue, pickLangValue, readLangs, writeLangs } from "@/utils";
import type { TyLangValues } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type FormProductPopupDetails = {
    categoryId?: string;
    onSuccess: () => void;
    editProductId?: string;
    duplicateProductId?: string;
    /** Fires the camera as soon as the form is ready — the menu card's "capture pictures" shortcut. */
    captureOnOpen?: boolean;
};

type Props = {
    onClose: () => void;
    details?: FormProductPopupDetails;
};

type FormState = {
    name: TyLangValues;
    description: TyLangValues;
    categoryIds: string[];
    prices: TyPriceRow[];
    hasDiscount: boolean;
    discountType: "percentage" | "fixed";
    discountAmount: string;
    active: boolean;
    hidden: boolean;
    pictures: File[];
    existingPictures: string[];
};

// --- Constants ----------------------------------------------------------------------------------

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": { backgroundColor: "var(--color-brand)", opacity: 1, border: 0 },
        },
    },
    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
};

// --- Logic --------------------------------------------------------------------------------------

export default function FormProductPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages, defaultLang } = useStoreLanguages();
    const addPopup = usePopup((state) => state.addComp);

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");

    const picturesRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);
    const hasAutoCapturedRef = useRef(false);

    // A capture is still being shrunk when this is on, and it is not in `form.pictures` yet
    const [isPreparingPictures, setIsPreparingPictures] = useState(false);

    const isEdit = Boolean(details?.editProductId);
    const fetchId = details?.editProductId ?? details?.duplicateProductId;

    const [form, setForm] = useState<FormState>({
        name: {},
        description: {},
        categoryIds: details?.categoryId ? [details.categoryId] : [],
        prices: [],
        hasDiscount: false,
        discountType: "percentage",
        discountAmount: "",
        active: true,
        hidden: false,
        pictures: [],
        existingPictures: [],
    });

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

    const setLangField = (key: "name" | "description", target: Lang, value: string) =>
        setForm((prev) => ({ ...prev, [key]: { ...prev[key], [target]: value } }));

    // Categories
    const { data: catData, loading: catLoading } = useFetch<TyApiResponse<{ categories: TyCategory[] }>>("/menu/categories");
    const categories = catData?.data?.categories ?? [];

    // Fetch product detail for edit / duplicate mode
    const { data: detailData, loading: detailLoading } = useFetch<TyApiResponse<{ product: TyProductDetail }>>(
        `/menu/products/${fetchId ?? "x"}`,
        {},
        fetchId ? undefined : false
    );

    useEffect(() => {
        const product = detailData?.data?.product;
        if (!product) return;
        setForm({
            name: readLangs(product.name, languages),
            description: readLangs(product.description, languages),
            categoryIds: product.category.map((c) => c._id),
            prices: product.prices.map((p) => ({
                key: crypto.randomUUID(),
                amount: String(p.amount),
                label: readLangs(p.label, languages),
                showLabel: p.showLabel,
                hasDiscount: p.discount?.active ?? false,
                discountType: p.discount?.type ?? "percentage",
                discountAmount: String(p.discount?.active ? p.discount.amount : ""),
            })),
            hasDiscount: product.discount?.active ?? false,
            discountType: product.discount?.type ?? "percentage",
            discountAmount: String(product.discount?.active ? product.discount.amount : ""),
            active: product.active,
            hidden: product.hidden,
            pictures: [],
            // In edit mode keep existing pictures; in duplicate mode start fresh
            existingPictures: isEdit ? product.pictures : [],
        });
    }, [detailData]);

    // The menu card's capture shortcut lands here. Wait for the product to load first — the effect
    // above resets `pictures`, so a shot taken before it arrives would be thrown away.
    useEffect(() => {
        if (!details?.captureOnOpen || hasAutoCapturedRef.current) return;
        if (fetchId && !detailData) return;
        const alreadyHeld = isEdit ? (detailData?.data?.product?.pictures.length ?? 0) : 0;
        if (alreadyHeld >= constants.maxProductPictures) return;
        hasAutoCapturedRef.current = true;
        cameraRef.current?.click();
    }, [details?.captureOnOpen, fetchId, detailData, isEdit]);

    // Submit — PUT for edit, POST for create / duplicate
    const submitPath = isEdit ? `/menu/products/${details!.editProductId}` : "/menu/products";
    const submitMethod = isEdit ? "PUT" : "POST";
    const {
        data: submitData,
        loading: submitting,
        error: submitError,
        invoke: submit,
    } = useFetch<TyApiResponse<unknown>>(submitPath, { method: submitMethod }, false);

    useEffect(() => {
        if (!submitData?.success) return;
        details?.onSuccess();
        onClose();
    }, [submitData]);

    // --------------------------------------------------------------------------------------------

    const openPricePopup = (index?: number) => {
        const initialPrice = index !== undefined ? form.prices[index] : undefined;
        const popupDetails: ProductSizePopupDetails = {
            initialPrice,
            onSave: (saved: TyPriceRow) => {
                if (index !== undefined) {
                    setForm((prev) => ({
                        ...prev,
                        prices: prev.prices.map((p, i) => (i === index ? saved : p)),
                    }));
                } else {
                    setForm((prev) => ({ ...prev, prices: [...prev.prices, saved] }));
                }
            },
        };
        addPopup({ name: "product-price-popup", Component: ProductSizePopup, details: popupDetails, anim: "scale" });
    };

    const removePrice = (index: number) => setForm((prev) => ({ ...prev, prices: prev.prices.filter((_, i) => i !== index) }));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Enter in any field submits the form, so the disabled button is not guard enough — saving
        // now would drop the picture still being shrunk
        if (isPreparingPictures) return;

        const name = writeLangs(form.name, languages, defaultLang);
        if (name.length === 0 || form.categoryIds.length === 0) return;

        const description = writeLangs(form.description, languages, defaultLang);

        const prices = form.prices
            .filter((p) => p.amount.trim() !== "")
            .map((p) => ({
                amount: parseFloat(p.amount) || 0,
                label: writeLangs(p.label, languages, defaultLang),
                showLabel: p.showLabel,
                discount:
                    p.hasDiscount && p.discountAmount
                        ? { active: true, type: p.discountType, amount: parseFloat(p.discountAmount) || 0 }
                        : { active: false, type: p.discountType, amount: 0 },
            }));

        const discount =
            form.hasDiscount && form.discountAmount ? { active: true, type: form.discountType, amount: parseFloat(form.discountAmount) || 0 } : null;

        const data: Record<string, unknown> = {
            name,
            description,
            category: form.categoryIds,
            prices,
            discount,
            active: form.active ? "true" : "",
            hidden: form.hidden ? "true" : "",
            // In edit mode, pass existing picture URLs to keep them
            ...(isEdit ? { pictures: form.existingPictures } : {}),
        };

        if (form.pictures.length > 0) data.newPictures = form.pictures;

        submit({ data });
    };

    const isSubmitDisabled = submitting || detailLoading || isPreparingPictures || !hasAnyLangValue(form.name) || form.categoryIds.length === 0;

    const pictureCount = form.pictures.length + form.existingPictures.length;
    const isPicturesFull = pictureCount >= constants.maxProductPictures;

    const title = isEdit ? t.formProduct.editTitle : t.formProduct.createTitle;
    const submitLabel = isEdit ? t.formProduct.submitEdit : t.formProduct.submit;

    return (
        <div className="form-product-popup">
            <div className="form-product-popup-header">
                <p className="form-product-popup-title">{title}</p>
                <button type="button" className="form-product-popup-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <form className="fpop-form" onSubmit={handleSubmit} noValidate>
                <div className="fpop-fields">
                    {/* ── Name ── */}
                    <MultiLangInput
                        label={t.formProduct.nameSection}
                        languages={languages}
                        values={form.name}
                        onChange={(target, value) => setLangField("name", target, value)}
                    />

                    {/* ── Description ── */}
                    <MultiLangInput
                        label={t.formProduct.descriptionSection}
                        languages={languages}
                        values={form.description}
                        onChange={(target, value) => setLangField("description", target, value)}
                    />

                    {/* ── Categories + Visibility ── */}
                    <InputGroups icon="/icons/inputs-group-icon.svg" title={t.formProduct.categoriesVisibilitySection}>
                        <FormControl fullWidth disabled={catLoading} sx={textFieldSx}>
                            <InputLabel>{t.formProduct.categoriesSection}</InputLabel>
                            <Select
                                multiple
                                value={form.categoryIds}
                                onChange={(e) => setField("categoryIds", e.target.value as string[])}
                                input={<OutlinedInput label={t.formProduct.categoriesSection} />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {(selected as string[]).map((id) => {
                                            const cat = categories.find((c) => c._id === id);
                                            return cat ? (
                                                <Chip
                                                    key={id}
                                                    label={cat.name}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "var(--color-shaded-brand)",
                                                        color: "var(--color-dark)",
                                                        border: "1px solid var(--color-mid-brand)",
                                                        height: "24px",
                                                        fontFamily: "Alexandria, sans-serif",
                                                        "& .MuiChip-label": { fontSize: "12px", fontWeight: 600, padding: "0 8px" },
                                                    }}
                                                />
                                            ) : null;
                                        })}
                                    </Box>
                                )}
                                MenuProps={{ slotProps: { paper: { style: { maxHeight: 48 * 4.5 + 8 } } } }}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat._id} value={cat._id}>
                                        <Checkbox
                                            checked={form.categoryIds.includes(cat._id)}
                                            sx={{
                                                color: "var(--color-gray-01)",
                                                "&.Mui-checked": { color: "var(--color-brand)" },
                                            }}
                                        />
                                        <ListItemText
                                            primary={cat.name}
                                            sx={{ "& .MuiTypography-root": { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } }}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <div className="fpop-visibility-row">
                            <div className="fpop-visibility-item">
                                <div className="fpop-toggle-info">
                                    <span className="fpop-toggle-label">{t.formProduct.activeLabel}</span>
                                    <span className="fpop-toggle-hint">{t.formProduct.activeHint}</span>
                                </div>
                                <Switch checked={form.active} onChange={(e) => setField("active", e.target.checked)} sx={switchSx} />
                            </div>
                            <div className="fpop-visibility-item">
                                <div className="fpop-toggle-info">
                                    <span className="fpop-toggle-label">{t.formProduct.hiddenLabel}</span>
                                    <span className="fpop-toggle-hint">{t.formProduct.hiddenHint}</span>
                                </div>
                                <Switch checked={form.hidden} onChange={(e) => setField("hidden", e.target.checked)} sx={switchSx} />
                            </div>
                        </div>
                    </InputGroups>

                    {/* ── Discount ── */}
                    <InputGroups
                        icon="/icons/inputs-group-icon.svg"
                        title={t.formProduct.discountSection}
                        toggle={{ checked: form.hasDiscount, onChange: (v) => setField("hasDiscount", v) }}
                    >
                        {form.hasDiscount && (
                            <div className="fpop-row">
                                <TextField
                                    select
                                    fullWidth
                                    label={t.formProduct.discountType}
                                    value={form.discountType}
                                    onChange={(e) => setField("discountType", e.target.value as "percentage" | "fixed")}
                                    sx={textFieldSx}
                                >
                                    <MenuItem value="percentage">{t.formProduct.discountPercentage}</MenuItem>
                                    <MenuItem value="fixed">{t.formProduct.discountFixed}</MenuItem>
                                </TextField>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t.formProduct.discountAmount}
                                    value={form.discountAmount}
                                    onChange={(e) => setField("discountAmount", e.target.value)}
                                    slotProps={{ htmlInput: { min: 0 } }}
                                    sx={textFieldSx}
                                />
                            </div>
                        )}
                    </InputGroups>

                    {/* ── Prices ── */}
                    <InputGroups
                        icon="/icons/inputs-group-icon.svg"
                        title={t.formProduct.pricesSection}
                        addButton={{ label: t.formProduct.addPrice, onClick: () => openPricePopup() }}
                    >
                        {form.prices.length > 0 ? (
                            <div className="fpop-price-list">
                                {form.prices.map((price, index) => (
                                    <div key={price.key} className="fpop-price-card">
                                        {price.showLabel && hasAnyLangValue(price.label) && (
                                            <div className="fpop-price-card-field">
                                                <div className="fpop-price-card-icon">
                                                    <img src="/icons/title-icon.svg" alt="" width={15} height={15} />
                                                </div>
                                                <span className="fpop-price-card-value">{pickLangValue(price.label, lang ?? defaultLang)}</span>
                                            </div>
                                        )}
                                        <div className="fpop-price-card-field">
                                            <div className="fpop-price-card-icon">
                                                <img src="/icons/price-icon.svg" alt="" width={15} height={15} />
                                            </div>
                                            <span className="fpop-price-card-value">{price.amount || "0"}</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="fpop-price-card-btn fpop-price-card-btn--edit"
                                            onClick={() => openPricePopup(index)}
                                        >
                                            <img src="/icons/edit-icon.svg" alt="" width={14} height={14} />
                                        </button>
                                        <button
                                            type="button"
                                            className="fpop-price-card-btn fpop-price-card-btn--delete"
                                            onClick={() => removePrice(index)}
                                        >
                                            <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="fpop-empty">{t.formProduct.emptyPrices}</div>
                        )}
                    </InputGroups>

                    {/* ── Pictures ── */}
                    <InputGroups
                        icon="/icons/inputs-group-icon.svg"
                        title={t.formProduct.pictureSection}
                        iconButton={{
                            icon: "/icons/camera-icon.svg",
                            label: t.formProduct.takePicture,
                            onClick: () => cameraRef.current?.click(),
                            disabled: isPicturesFull || isPreparingPictures,
                        }}
                        addButton={{
                            label: t.formProduct.addPicture,
                            onClick: () => picturesRef.current?.click(),
                            disabled: isPicturesFull || isPreparingPictures,
                        }}
                    >
                        {pictureCount === 0 && <div className="fpop-empty">{t.formProduct.emptyPictures}</div>}
                        <FilesInput
                            files={form.pictures}
                            existingUrls={form.existingPictures}
                            onChange={(files) => setField("pictures", files)}
                            onRemoveExisting={(url) =>
                                setField(
                                    "existingPictures",
                                    form.existingPictures.filter((u) => u !== url)
                                )
                            }
                            inputRef={picturesRef}
                            maxFiles={constants.maxProductPictures}
                            withCamera
                            cameraInputRef={cameraRef}
                            onPreparingChange={setIsPreparingPictures}
                        />
                        {isPicturesFull && <p className="fpop-pictures-hint">{t.formProduct.picturesLimitHint}</p>}
                    </InputGroups>
                </div>

                {submitError && <p className="fpop-error">{submitError}</p>}

                <div className="fpop-footer">
                    <button type="button" className="fpop-btn fpop-btn--cancel" onClick={onClose} disabled={submitting}>
                        {t.formProduct.cancel}
                    </button>
                    <button type="submit" className="fpop-btn fpop-btn--submit" disabled={isSubmitDisabled}>
                        {submitting ? <span className="fpop-spinner" /> : submitLabel}
                    </button>
                </div>
            </form>
        </div>
    );
}
